<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <FormulateInput
            id="name"
            v-model="filters.name"
            :label="$t('Nome do Subgrupo')"
            :placeholder="$t('Nome')"
          />
        </b-col>
        <b-col md="6">
          <FormulateInput
            id="group-id"
            v-model="filters.groupId"
            type="vue-select"
            :label="$t('Grupo')"
            :placeholder="$t('Grupo')"
            :options="getComboGroups"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="subgroup-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('SUBGROUP.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="productGroups"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getSubgroups"
      >
        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :is-active="row.item.active"
            :show-update="$can('Update', 'ProductSubgroup')"
            :show-delete="$can('Delete', 'ProductSubgroup')"
            :show-activate="$can('Activate', 'ProductSubgroup')"
            :show-deactivate="$can('Deactivate', 'ProductSubgroup')"
            @update="showUpdateGroupSidebar(row)"
            @delete="deleteGroup(row.item)"
            @activate="activateDeactivateGroup(row)"
            @deactivate="activateDeactivateGroup(row)"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="product-subgroup-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <product-subgroup-sidebar
      ref="subgroup_sidebar"
      @save="filterData"
    />

    <fab
      v-if="$can('Create', 'ProductSubgroup')"
      :main-tooltip="$t('Adicionar Subgrupo')"
      @click="showCreateGroupSidebar"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary, EFilters, EGridActions } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { uploader } from '@/mixins'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import ProductSubgroupSidebar from './ProductSubgroupSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    ProductSubgroupSidebar,
    EFilters,
    EGridActions,
    EStatusBadge,
  },

  mixins: [uploader],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      edit: false,
      saving: false,
      groups: [],
      subgroup: {
        id: null,
        name: '',
        group: null,
      },
    }
  },
  computed: {
    ...mapState('pages/catalog/productSubgroups', {
      productGroups: 'productSubgroups',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),
    ...mapGetters('pages/catalog/products', ['getComboGroups']),
    selectedSubgroupId() {
      return this.subgroup?.id
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Grupo'),
          key: 'group.name',
          trClass: 'text-center',
          tdClass: 'text-left',
          thStyle: { width: '250px' },
          sortable: true,
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          trClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/catalog/productSubgroups', [
      'fetchProductSubgroups',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),
    ...mapActions('pages/catalog/products', { comboFetchDomainData: 'fetchDomainData' }),

    async getGroups() {
      try {
        this.fetching = true
        if (this.getComboGroups?.length === 0) {
          await this.comboFetchDomainData('product-groups')
        }
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.fetching = false
      }
    },
    async getSubgroups() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchProductSubgroups()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getSubgroups()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    showCreateGroupSidebar() {
      this.subgroup = {}
      this.$refs.subgroup_sidebar.show()
    },

    async showUpdateGroupSidebar(row) {
      this.$refs.subgroup_sidebar.show({ id: row.item.id })
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getSubgroups()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getSubgroups()
    },

    async deleteGroup(subgroup) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.$http.delete(`/api/product-subgroups/${subgroup.id}`)
          await this.comboFetchDomainData('product-groups')
          this.getSubgroups()
          this.showSuccess({ message: this.$t('Subgrupo removido com sucesso') })
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    async activateDeactivateGroup(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const path = row.item.active ? 'deactivate' : 'activate'
          await this.$http({
            url: `/api/product-subgroups/${path}/${row.item.id}`,
            method: 'PUT',
          })
          await this.comboFetchDomainData('product-groups')
          this.getSubgroups()
          this.showSuccess({
            message: this.$t(`${row.item.active === true ? 'Inativado' : 'Ativado'} com sucesso`),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
